import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Seo from "@/components/base/Seo";
import PageHero from "@/components/feature/PageHero";
import LeadCard from "@/components/base/LeadCard";
import Reveal from "@/components/base/Reveal";
import { leads } from "@/mocks/leads";
import { categories } from "@/mocks/categories";

const allOption = { id: "all", name: "全部线索" };

export default function Leads() {
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get("category") ?? "all";

  const [activeCategory, setActiveCategory] = useState(categoryParam);
  const [keyword, setKeyword] = useState("");
  const [visibleCount, setVisibleCount] = useState(6);

  useEffect(() => {
    setActiveCategory(categoryParam);
    setVisibleCount(6);
  }, [categoryParam]);

  const filtered = useMemo(() => {
    const kw = keyword.trim();
    return leads.filter((lead) => {
      const matchCategory = activeCategory === "all" || lead.categoryId === activeCategory;
      const matchKeyword =
        kw.length === 0 ||
        lead.title.includes(kw) ||
        lead.summary.includes(kw) ||
        lead.region.includes(kw) ||
        lead.category.includes(kw);
      return matchCategory && matchKeyword;
    });
  }, [activeCategory, keyword]);

  const visible = filtered.slice(0, visibleCount);
  const activeName =
    categories.find((item) => item.id === activeCategory)?.name ?? allOption.name;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "线索广场 - 爆料51",
      url: "https://guilinzsw.cn/leads",
      description: "按分类浏览爆料51正在征集的公众线索，查看悬赏金额与征集状态。",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://guilinzsw.cn/" },
        { "@type": "ListItem", position: 2, name: "线索广场", item: "https://guilinzsw.cn/leads" },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "爆料51 正在征集的线索",
      itemListElement: leads.map((lead, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: lead.title,
        url: "https://guilinzsw.cn/leads",
      })),
    },
  ];

  return (
    <>
      <Seo
        title="线索广场 | 爆料51 正在征集的公众线索"
        description="浏览爆料51线索广场，按民生、消费维权、环境安全、财经等分类查看正在征集的线索，了解悬赏金额、征集状态与参与热度。"
        keywords="爆料51线索广场,线索征集,找线索,悬赏线索,公众线索"
        path="/leads"
        jsonLd={jsonLd}
      />
      <PageHero
        eyebrow="线索广场"
        title="正在征集的公众线索"
        description="这里汇总了爆料51 当前开放征集的全部线索。可按分类筛选、按关键词搜索，查看每条线索的悬赏金额与征集状态。"
        crumbs={[{ label: "线索广场" }]}
      />

      <section className="w-full bg-background-50 py-10 md:py-14">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-8">
          <div className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100 p-4 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap gap-2">
              {[allOption, ...categories].map((item) => {
                const isActive = activeCategory === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      setActiveCategory(item.id);
                      setVisibleCount(6);
                    }}
                    className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                      isActive
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-50 text-foreground-700 hover:bg-background-200"
                    }`}
                  >
                    {item.name}
                  </button>
                );
              })}
            </div>
            <div className="relative w-full md:w-72">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400" />
              <input
                type="search"
                value={keyword}
                onChange={(event) => {
                  setKeyword(event.target.value);
                  setVisibleCount(6);
                }}
                placeholder="搜索线索标题、地区或分类"
                className="h-10 w-full rounded-md border border-background-300 bg-background-50 pl-9 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
              />
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between">
            <p className="text-sm text-foreground-600">
              当前分类：<span className="font-medium text-foreground-900">{activeName}</span>
              <span className="ml-2 text-foreground-400">共 {filtered.length} 条</span>
            </p>
            {keyword ? (
              <button
                type="button"
                onClick={() => setKeyword("")}
                className="text-xs text-primary-600 transition-colors hover:text-primary-700"
              >
                清除搜索
              </button>
            ) : null}
          </div>

          {visible.length > 0 ? (
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {visible.map((lead, index) => (
                <Reveal key={lead.id} delay={index * 50}>
                  <LeadCard lead={lead} />
                </Reveal>
              ))}
            </div>
          ) : (
            <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-search-eye-line text-2xl" />
              </span>
              <h2 className="mt-4 font-heading text-base font-bold text-foreground-950">没有找到匹配的线索</h2>
              <p className="mt-2 max-w-md text-sm text-foreground-600">
                试试更换分类或缩短关键词。如果你手上有值得关注的信息，也欢迎直接提交给我们。
              </p>
              <button
                type="button"
                onClick={() => {
                  setKeyword("");
                  setActiveCategory("all");
                  setVisibleCount(6);
                }}
                className="mt-5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                重置筛选条件
              </button>
            </div>
          )}

          {visibleCount < filtered.length ? (
            <div className="mt-10 flex justify-center">
              <button
                type="button"
                onClick={() => setVisibleCount((count) => count + 3)}
                className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                加载更多线索
              </button>
            </div>
          ) : null}
        </div>
      </section>
    </>
  );
}